import React, { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const POLL_MS = 30000;

/**
 * Approval notifications bell — polls /api/approvals/counts/me every 30s.
 * Shows a badge when the current user has pending actions in the Approvals module.
 * Click → navigates to the role-appropriate Approvals page (or to a tab in the dashboard).
 */
export const ApprovalsBell = ({ api, user, targetPath = '/approvals', onClick }) => {
  const [count, setCount] = useState(0);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchCount = async () => {
    try {
      const res = await api.get('/approvals/counts/me');
      if (!mountedRef.current) return;
      setCount(res.data?.pending_action || 0);
      setError(false);
    } catch (e) {
      if (mountedRef.current) setError(true);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_MS);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(targetPath);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted/60 transition-colors"
      title={count > 0 ? `${count} pending approval${count === 1 ? '' : 's'}` : 'No pending approvals'}
      aria-label="Approvals notifications"
      data-testid="approvals-bell"
    >
      <Bell className={`w-4 h-4 ${count > 0 ? 'text-primary' : 'text-muted-foreground'}`} />
      {count > 0 && !error && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background"
          data-testid="approvals-bell-badge"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

export default ApprovalsBell;
